import { useEffect, useRef, useState, type FormEvent, type PointerEvent as ReactPointerEvent, type RefObject } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, Plus, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import MessageField from "@/components/messages/MessageField";
import MessageHtml from "@/components/messages/MessageHtml";
import { api, errorMessage, post } from "@/lib/api";
import { refreshUnreadNavCount } from "@/components/messages/unreadNavCount";
import { messagePlain, messageReady } from "@/lib/messageBody";
import "./Schools.css";

type OfficeEntry = {
  id: string;
  parentId: string | null;
  body: string;
  authorName: string | null;
  authorRole: string;
  at: string;
};

type OfficeSummary = {
  id: string;
  schoolId: string;
  schoolName: string | null;
  title: string;
  parentName: string | null;
  students: Array<{ studentUserId: string; studentName: string }>;
  lastBody: string;
  lastAuthorName: string | null;
  lastAuthorRole: string | null;
  lastAt: string;
  messageCount: number;
};

type OfficeThread = OfficeSummary & { entries: OfficeEntry[] };

function whenLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function whoLabel(name: string | null, role: string | null) {
  if (role === "instructor") return name ? `${name} · Office` : "Office";
  return name || "Parent";
}

function accessSentence(parts: string[]) {
  if (parts.length === 0) return "You currently have access to none of these yet.";
  if (parts.length === 1) return `You currently have access to ${parts[0]}.`;
  if (parts.length === 2) return `You currently have access to ${parts[0]} and ${parts[1]}.`;
  return `You currently have access to ${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}.`;
}

type Mailbox = "office" | "direct";

function byTime(a: { at: string }, b: { at: string }) {
  return new Date(a.at).getTime() - new Date(b.at).getTime();
}

function childrenOf<T extends { parentId: string | null; at: string }>(entries: T[], parentId: string) {
  return entries.filter((entry) => entry.parentId === parentId).sort(byTime);
}

function Spinner() {
  return (
    <div className="al-office-loading" role="status">
      <span className="al-office-spinner" aria-hidden="true" />
      <span className="al-office-loading-label">Loading</span>
    </div>
  );
}

export default function Messages() {
  const { capabilities, session } = useAuth();
  const office =
    session?.acuityLearn?.messageKinds != null
      ? session.acuityLearn.messageKinds.office === true
      : Boolean(capabilities?.receiveMessages);
  const canReply = Boolean(capabilities?.replyMessages && office);
  const showDirect = Boolean(
    capabilities?.sendDm ||
      capabilities?.replyDm ||
      capabilities?.sendStudentTeacher ||
      capabilities?.sendStudentRelationship,
  );
  const access = [showDirect ? "the inbox" : null, office ? "the office inbox" : null].filter(
    (part): part is string => Boolean(part),
  );
  const [tab, setTab] = useState<Mailbox>(showDirect ? "direct" : "office");
  const [rows, setRows] = useState<OfficeSummary[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [thread, setThread] = useState<OfficeThread | null>(null);
  const [rootReply, setRootReply] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [popReply, setPopReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const popRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (tab === "office" && !office && showDirect) setTab("direct");
    if (tab === "direct" && !showDirect && office) setTab("office");
  }, [tab, office, showDirect]);

  useEffect(() => {
    if (!office) return undefined;
    let cancel = false;
    api<{ success: boolean; data: OfficeSummary[] }>("/api/schools/messages")
      .then((env) => {
        if (!cancel) setRows(env.data || []);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [office]);

  useEffect(() => {
    if (!openId) return undefined;
    let cancel = false;
    setThread(null);
    setError("");
    setReplyTo(null);
    setPopReply("");
    setRootReply("");
    api<{ success: boolean; data: OfficeThread }>(`/api/schools/messages/${openId}`)
      .then((env) => {
        if (!cancel) setThread(env.data);
        void refreshUnreadNavCount();
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [openId]);

  useEffect(() => {
    if (replyTo) popRef.current?.focus();
  }, [replyTo]);

  const patchList = (next: OfficeThread) => {
    const latest = [...next.entries].sort(byTime).at(-1);
    setRows((current) =>
      (current || [])
        .map((row) =>
          row.id === next.id
            ? {
                ...row,
                lastBody: messagePlain(latest?.body || row.lastBody),
                lastAuthorName: latest?.authorName || null,
                lastAuthorRole: latest?.authorRole || null,
                lastAt: latest?.at || row.lastAt,
                messageCount: next.entries.length,
              }
            : row,
        )
        .sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime()),
    );
  };

  const sendReply = async (parentId: string, body: string) => {
    if (!openId || busy || !body.trim()) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: OfficeThread }>(
        `/api/schools/messages/${openId}/replies`,
        { body, parentId },
      );
      setThread(env.data);
      patchList(env.data);
      if (parentId === env.data.id) setRootReply("");
      else {
        setReplyTo(null);
        setPopReply("");
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    setOpenId(null);
    setReplyTo(null);
    setPopReply("");
    setError("");
  };

  const root = thread?.entries.find((entry) => entry.id === thread.id)
    || thread?.entries.find((entry) => !entry.parentId)
    || null;

  return (
    <div className="al-schools">
      <header className="al-schools-header">
        <div className="al-schools-header-text">
          <h1>Messages</h1>
          <p className="al-schools-sub">
            Home for direct messages, office outreach, and support outreach. {accessSentence(access)}
          </p>
        </div>
      </header>
      {office || showDirect ? (
        <div className="al-msg-tabs" role="tablist" aria-label="Mailboxes">
          {showDirect ? (
            <button
              type="button"
              role="tab"
              aria-selected={tab === "direct"}
              className={`al-msg-tab${tab === "direct" ? " is-on" : ""}`}
              onClick={() => {
                setTab("direct");
                goBack();
              }}
            >
              Inbox
            </button>
          ) : null}
          {office ? (
            <button
              type="button"
              role="tab"
              aria-selected={tab === "office"}
              className={`al-msg-tab${tab === "office" ? " is-on" : ""}`}
              onClick={() => {
                setTab("office");
                goBack();
              }}
            >
              Office
            </button>
          ) : null}
        </div>
      ) : null}
      {error && !openId && tab === "office" ? <p className="al-schools-sub">{error}</p> : null}
      {tab === "direct" && showDirect ? (
        <DirectMailbox
          canSend={Boolean(capabilities?.sendDm)}
          canReply={Boolean(
            capabilities?.replyDm || capabilities?.sendStudentTeacher || capabilities?.sendStudentRelationship,
          )}
          canCircle={Boolean(capabilities?.sendStudentTeacher || capabilities?.sendStudentRelationship)}
          canSearch={Boolean(capabilities?.searchPeople)}
          myUserId={session?.user?.id == null ? "" : String(session.user.id)}
        />
      ) : null}
      {tab === "office" && office ? (
      <div className="al-office-stage">
        <div className={`al-office-track${openId ? " is-open" : ""}`}>
          <div className="al-office-pane">
            {rows == null && !error ? <Spinner /> : null}
            {rows && rows.length === 0 ? <p className="al-schools-sub">No messages yet.</p> : null}
            {rows && rows.length > 0 ? (
              <ul className="al-office-list">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button type="button" className="al-office-item al-office-open" onClick={() => setOpenId(row.id)}>
                      <div className="al-office-item-top">
                        <strong>{row.title || row.parentName || "Conversation"}</strong>
                        <span>{whenLabel(row.lastAt)}</span>
                      </div>
                      <p className="al-office-meta">
                        {[row.parentName, row.students.map((student) => student.studentName).join(", "), row.schoolName]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <p className="al-office-body al-office-snippet">{messagePlain(row.lastBody)}</p>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="al-office-pane">
            <div className="al-office-thread">
              <button type="button" className="al-office-back" onClick={goBack} aria-label="Back">
                <ChevronLeft size={18} aria-hidden />
              </button>
              {error && openId ? <p className="al-schools-sub">{error}</p> : null}
              {openId && !thread && !error ? <Spinner /> : null}
              {thread && root ? (
                <>
                  <article className="al-office-msg al-office-root">
                    <div className="al-office-item-top">
                      <strong>{thread.title || "Conversation"}</strong>
                      <span>{whenLabel(root.at)}</span>
                    </div>
                    <p className="al-office-meta">
                      {[
                        whoLabel(root.authorName || thread.parentName, root.authorRole),
                        thread.students.map((student) => student.studentName).join(", "),
                        thread.schoolName,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <MessageHtml body={root.body} className="al-office-body" />
                  </article>
                  <div className="al-office-timeline">
                    {childrenOf(thread.entries, root.id).map((entry) => (
                      <TimelineMessage
                        key={entry.id}
                        entry={entry}
                        entries={thread.entries}
                        canReply={canReply}
                        replyTo={replyTo}
                        popReply={popReply}
                        popRef={popRef}
                        busy={busy}
                        onOpen={(id) => {
                          setReplyTo((current) => (current === id ? null : id));
                          setPopReply("");
                        }}
                        onPopReply={setPopReply}
                        onSend={(parentId) => void sendReply(parentId, popReply)}
                      />
                    ))}
                  </div>
                  {canReply ? (
                    <form
                      className="al-office-reply"
                      onSubmit={(event: FormEvent) => {
                        event.preventDefault();
                        void sendReply(root.id, rootReply);
                      }}
                    >
                      <MessageField
                        id="office-reply"
                        label="Reply"
                        value={rootReply}
                        onChange={setRootReply}
                        placeholder="Reply to the first message"
                        disabled={busy}
                      />
                      <button type="submit" disabled={busy || !messageReady(rootReply)}>
                        {busy ? "Sending…" : "Send reply"}
                      </button>
                    </form>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      ) : null}
    </div>
  );
}

type DirectSummary = {
  id: string;
  otherUserId: string | null;
  otherName: string;
  lastBody: string;
  lastAt: string;
};

type DirectEntry = OfficeEntry & { authorUserId: string | null };

type DirectThread = DirectSummary & { entries: DirectEntry[] };

type PersonHit = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
};

function personLabel(person: PersonHit) {
  const name = [person.first_name, person.last_name].filter(Boolean).join(" ").trim();
  return name || person.email || "Person";
}

function directWho(entry: DirectEntry, myUserId: string) {
  if (myUserId && entry.authorUserId === myUserId) return "You";
  return entry.authorName || "Person";
}

function DirectMailbox({
  canSend,
  canReply,
  canSearch,
  canCircle,
  myUserId,
}: {
  canSend: boolean;
  canReply: boolean;
  canSearch: boolean;
  canCircle: boolean;
  myUserId: string;
}) {
  const [rows, setRows] = useState<DirectSummary[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [thread, setThread] = useState<DirectThread | null>(null);
  const [email, setEmail] = useState("");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<PersonHit[]>([]);
  const [picked, setPicked] = useState<PersonHit | null>(null);
  const [locked, setLocked] = useState<{ userId: string; name: string; studentUserId: string } | null>(null);
  const [rootReply, setRootReply] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [popReply, setPopReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dockMounted, setDockMounted] = useState(false);
  const [dockOn, setDockOn] = useState(false);
  const [dockSize, setDockSize] = useState<{ w: number; h: number } | null>(null);
  const popRef = useRef<HTMLTextAreaElement>(null);
  const composeFieldRef = useRef<HTMLInputElement>(null);
  const composeBodyRef = useRef<HTMLTextAreaElement>(null);
  const dockRef = useRef<HTMLElement>(null);
  const dockTimer = useRef<number | null>(null);
  const dockFrame = useRef<number | null>(null);
  const searchOn = canSearch;
  const [params, setParams] = useSearchParams();
  const withUser = params.get("with") || "";
  const toEmail = params.get("email") || "";
  const toName = params.get("name") || "";
  const studentUserId = params.get("student") || "";
  const openedFromLink = useRef(false);
  const composeOn = canSend || Boolean(locked);

  const clearDockMotion = () => {
    if (dockTimer.current != null) {
      window.clearTimeout(dockTimer.current);
      dockTimer.current = null;
    }
    if (dockFrame.current != null) {
      window.cancelAnimationFrame(dockFrame.current);
      dockFrame.current = null;
    }
  };

  const openCompose = () => {
    clearDockMotion();
    setDockMounted(true);
    dockFrame.current = window.requestAnimationFrame(() => {
      dockFrame.current = window.requestAnimationFrame(() => {
        dockFrame.current = null;
        setDockOn(true);
      });
    });
  };

  const closeCompose = () => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setDockOn(false);
    clearDockMotion();
    if (reduce) {
      setDockMounted(false);
      return;
    }
    dockTimer.current = window.setTimeout(() => {
      dockTimer.current = null;
      setDockMounted(false);
    }, 360);
  };

  const onResizeDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    const dock = dockRef.current;
    if (!dock) return;
    const rect = dock.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = rect.width;
    const startH = rect.height;
    const maxW = Math.max(280, rect.right - 12);
    const maxH = Math.max(240, rect.bottom - 12);
    handle.setPointerCapture(event.pointerId);
    const move = (ev: PointerEvent) => {
      const nextW = Math.round(Math.min(maxW, Math.max(280, startW + (startX - ev.clientX))));
      const nextH = Math.round(Math.min(maxH, Math.max(240, startH + (startY - ev.clientY))));
      setDockSize({ w: nextW, h: nextH });
    };
    const up = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
      handle.removeEventListener("pointercancel", up);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
    handle.addEventListener("pointercancel", up);
  };

  const load = () => {
    api<{ success: boolean; data: DirectSummary[] }>("/api/schools/direct")
      .then((env) => setRows(env.data || []))
      .catch((err) => setError(errorMessage(err)));
  };

  useEffect(() => {
    load();
    return () => clearDockMotion();
  }, []);

  useEffect(() => {
    if (openedFromLink.current || rows == null || (!withUser && !toEmail)) return;
    if (studentUserId && !canCircle && !canSend) return;
    openedFromLink.current = true;
    const existing = withUser
      ? rows.find((row) => row.otherUserId && String(row.otherUserId) === withUser)
      : undefined;
    if (existing) {
      setOpenId(existing.id);
    } else if (studentUserId && withUser && canCircle) {
      setLocked({ userId: withUser, name: toName || toEmail || "Person", studentUserId });
      openCompose();
    } else if (canSend) {
      if (toEmail) {
        setEmail(toEmail);
        setQuery(toEmail);
      }
      openCompose();
    }
    const next = new URLSearchParams(params);
    next.delete("with");
    next.delete("email");
    next.delete("name");
    next.delete("student");
    setParams(next, { replace: true });
  }, [rows, withUser, toEmail, toName, studentUserId, canCircle, canSend, params, setParams]);

  useEffect(() => {
    if (!dockOn) return undefined;
    const timer = window.setTimeout(() => {
      (composeFieldRef.current || composeBodyRef.current)?.focus();
    }, 40);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeCompose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKey);
    };
  }, [dockOn]);

  useEffect(() => {
    if (!openId) return undefined;
    let cancel = false;
    setThread(null);
    setError("");
    setReplyTo(null);
    setPopReply("");
    setRootReply("");
    api<{ success: boolean; data: DirectThread }>(`/api/schools/direct/${openId}`)
      .then((env) => {
        if (!cancel) setThread(env.data);
        void refreshUnreadNavCount();
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [openId]);

  useEffect(() => {
    if (!searchOn || picked || query.trim().length < 2) {
      setHits([]);
      return undefined;
    }
    let cancel = false;
    const timer = window.setTimeout(() => {
      api<{ success: boolean; data: PersonHit[] }>(
        `/api/schools/direct/search?q=${encodeURIComponent(query.trim())}`,
      )
        .then((env) => {
          if (!cancel) setHits(env.data || []);
        })
        .catch(() => {
          if (!cancel) setHits([]);
        });
    }, 250);
    return () => {
      cancel = true;
      window.clearTimeout(timer);
    };
  }, [query, picked, searchOn]);

  useEffect(() => {
    if (replyTo) popRef.current?.focus();
  }, [replyTo]);

  const sendNew = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !rootReply.trim()) return;
    if (locked) {
      /* recipient is the person chosen on My Students */
    } else if (searchOn && !picked) return;
    else if (!searchOn && !email.trim()) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: DirectThread }>("/api/schools/direct", {
        userId: locked?.userId || picked?.id,
        email: locked || picked ? undefined : email.trim(),
        studentUserId: locked?.studentUserId,
        body: rootReply,
      });
      setRootReply("");
      setEmail("");
      setQuery("");
      setPicked(null);
      setLocked(null);
      setDockOn(false);
      setDockMounted(false);
      setOpenId(env.data.id);
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (parentId: string, body: string) => {
    if (!openId || busy || !body.trim()) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: DirectThread }>(
        `/api/schools/direct/${openId}/replies`,
        { body, parentId },
      );
      setThread(env.data);
      load();
      if (parentId === env.data.id) setRootReply("");
      else {
        setReplyTo(null);
        setPopReply("");
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const root = thread?.entries.find((entry) => entry.id === thread.id)
    || thread?.entries.find((entry) => !entry.parentId)
    || null;

  const openConversation = (id: string) => {
    clearDockMotion();
    setDockOn(false);
    setDockMounted(false);
    setOpenId(id);
  };

  return (
    <>
      {error && !openId && !dockMounted ? <p className="al-schools-sub">{error}</p> : null}
      <div className="al-office-stage">
        <div className={`al-office-track${openId ? " is-open" : ""}`}>
          <div className={`al-office-pane${canSend ? " al-msg-inbox-pane" : ""}`}>
            {rows == null && !error ? <Spinner /> : null}
            {rows && rows.length === 0 ? <p className="al-schools-sub">Nothing in the inbox yet.</p> : null}
            {rows && rows.length > 0 ? (
              <ul className="al-office-list">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button type="button" className="al-office-item al-office-open" onClick={() => openConversation(row.id)}>
                      <div className="al-office-item-top">
                        <strong>{row.otherName}</strong>
                        <span>{whenLabel(row.lastAt)}</span>
                      </div>
                      <p className="al-office-body al-office-snippet">{messagePlain(row.lastBody)}</p>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="al-office-pane">
            <div className="al-office-thread">
              <button type="button" className="al-office-back" onClick={() => setOpenId(null)} aria-label="Back">
                <ChevronLeft size={18} aria-hidden />
              </button>
              {error && openId ? <p className="al-schools-sub">{error}</p> : null}
              {openId && !thread && !error ? <Spinner /> : null}
              {thread && root ? (
                <>
                  <article className="al-office-msg al-office-root">
                    <div className="al-office-item-top">
                      <strong>{thread.otherName}</strong>
                      <span>{whenLabel(root.at)}</span>
                    </div>
                    <p className="al-office-meta">{directWho(root, myUserId)}</p>
                    <MessageHtml body={root.body} className="al-office-body" />
                  </article>
                  <div className="al-office-timeline">
                    {childrenOf(thread.entries, root.id).map((entry) => (
                      <DirectLine
                        key={entry.id}
                        entry={entry}
                        entries={thread.entries}
                        myUserId={myUserId}
                        canReply={canReply}
                        replyTo={replyTo}
                        popReply={popReply}
                        popRef={popRef}
                        busy={busy}
                        onOpen={(id) => {
                          setReplyTo((current) => (current === id ? null : id));
                          setPopReply("");
                        }}
                        onPopReply={setPopReply}
                        onSend={(parentId) => void sendReply(parentId, popReply)}
                      />
                    ))}
                  </div>
                  {canReply ? (
                    <form
                      className="al-office-reply"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void sendReply(root.id, rootReply);
                      }}
                    >
                      <MessageField
                        id="dm-reply"
                        label="Reply"
                        value={rootReply}
                        onChange={setRootReply}
                        placeholder="Reply to the first message"
                        disabled={busy}
                      />
                      <button type="submit" disabled={busy || !messageReady(rootReply)}>
                        {busy ? "Sending…" : "Send reply"}
                      </button>
                    </form>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {canSend && !openId ? (
        <button
          type="button"
          className={`al-msg-fab${dockOn ? " is-away" : ""}${dockMounted ? " is-holding" : ""}`}
          tabIndex={dockMounted ? -1 : 0}
          aria-hidden={dockMounted ? true : undefined}
          onClick={openCompose}
        >
          <Plus size={14} strokeWidth={2.25} aria-hidden />
          New Conversation
        </button>
      ) : null}
      {composeOn && !openId && dockMounted ? (
        <aside
          ref={dockRef}
          className={`al-msg-dock${dockOn ? " is-on" : ""}`}
          style={dockSize ? { width: dockSize.w, height: dockSize.h, maxHeight: "none" } : undefined}
          role="dialog"
          aria-label="New conversation"
          aria-hidden={dockOn ? undefined : true}
        >
          <button
            type="button"
            className="al-msg-dock-resize"
            aria-label="Resize"
            onPointerDown={onResizeDown}
          />
          <div className="al-msg-dock-bar">
            <h2>New conversation</h2>
            <button type="button" className="al-msg-dock-close" onClick={closeCompose} aria-label="Close">
              <X size={16} aria-hidden />
            </button>
          </div>
          <form className="al-office-reply al-msg-dock-form" onSubmit={(event) => void sendNew(event)}>
            {error ? <p className="al-msg-dock-error">{error}</p> : null}
            {locked ? (
              <>
                <label htmlFor="dm-locked">To</label>
                <p id="dm-locked" className="al-office-meta al-msg-dock-picked">
                  {locked.name}
                </p>
              </>
            ) : searchOn ? (
              <>
                <label htmlFor="dm-search">To</label>
                {picked ? (
                  <p className="al-office-meta al-msg-dock-picked">
                    {personLabel(picked)}
                    <button type="button" className="al-msg-clear" onClick={() => setPicked(null)}>
                      Change
                    </button>
                  </p>
                ) : (
                  <input
                    id="dm-search"
                    ref={composeFieldRef}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Name or email"
                    autoComplete="off"
                  />
                )}
                {hits.length > 0 ? (
                  <ul className="al-msg-hits">
                    {hits.map((person) => (
                      <li key={person.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setPicked(person);
                            setHits([]);
                          }}
                        >
                          {personLabel(person)}
                          {person.email ? <span>{person.email}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : (
              <>
                <label htmlFor="dm-email">To</label>
                <input
                  id="dm-email"
                  ref={composeFieldRef}
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Type an email"
                  autoComplete="off"
                />
              </>
            )}
            <p className="al-msg-dock-note">
              If a conversation with this person already exists, this message is added to it. A new thread is not created.
            </p>
            <MessageField
              id="dm-body"
              label="Message"
              value={rootReply}
              onChange={setRootReply}
              placeholder="Write a message"
              disabled={busy}
              textareaRef={composeBodyRef}
            />
            <div className="al-msg-actions">
              <button
                type="submit"
                disabled={busy || !messageReady(rootReply) || (locked ? false : searchOn ? !picked : !email.trim())}
              >
                {busy ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        </aside>
      ) : null}
    </>
  );
}

function DirectLine({
  entry,
  entries,
  myUserId,
  canReply,
  replyTo,
  popReply,
  popRef,
  busy,
  onOpen,
  onPopReply,
  onSend,
}: {
  entry: DirectEntry;
  entries: DirectEntry[];
  myUserId: string;
  canReply: boolean;
  replyTo: string | null;
  popReply: string;
  popRef: RefObject<HTMLTextAreaElement>;
  busy: boolean;
  onOpen: (id: string) => void;
  onPopReply: (value: string) => void;
  onSend: (parentId: string) => void;
}) {
  const open = replyTo === entry.id;
  const nested = childrenOf(entries, entry.id);
  return (
    <article className={`al-office-msg${entry.authorUserId === myUserId ? " is-office" : ""}${open ? " is-open" : ""}`}>
      {canReply ? (
        <button type="button" className="al-office-msg-hit" onClick={() => onOpen(entry.id)}>
          <div className="al-office-item-top">
            <strong>{directWho(entry, myUserId)}</strong>
            <span>{whenLabel(entry.at)}</span>
          </div>
          <MessageHtml body={entry.body} className="al-office-body" />
        </button>
      ) : (
        <div className="al-office-msg-hit">
          <div className="al-office-item-top">
            <strong>{directWho(entry, myUserId)}</strong>
            <span>{whenLabel(entry.at)}</span>
          </div>
          <MessageHtml body={entry.body} className="al-office-body" />
        </div>
      )}
      {open ? (
        <form
          className="al-office-pop"
          onSubmit={(event) => {
            event.preventDefault();
            onSend(entry.id);
          }}
        >
          <MessageField
            id={`dm-pop-${entry.id}`}
            label="Reply to this message"
            value={popReply}
            onChange={onPopReply}
            placeholder="Write a reply"
            disabled={busy}
            textareaRef={popRef}
          />
          <button type="submit" disabled={busy || !messageReady(popReply)}>
            {busy ? "Sending…" : "Reply here"}
          </button>
        </form>
      ) : null}
      {nested.length > 0 ? (
        <div className="al-office-nest">
          {nested.map((child) => (
            <DirectLine
              key={child.id}
              entry={child}
              entries={entries}
              myUserId={myUserId}
              canReply={canReply}
              replyTo={replyTo}
              popReply={popReply}
              popRef={popRef}
              busy={busy}
              onOpen={onOpen}
              onPopReply={onPopReply}
              onSend={onSend}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}

function TimelineMessage({
  entry,
  entries,
  canReply,
  replyTo,
  popReply,
  popRef,
  busy,
  onOpen,
  onPopReply,
  onSend,
}: {
  entry: OfficeEntry;
  entries: OfficeEntry[];
  canReply: boolean;
  replyTo: string | null;
  popReply: string;
  popRef: RefObject<HTMLTextAreaElement>;
  busy: boolean;
  onOpen: (id: string) => void;
  onPopReply: (value: string) => void;
  onSend: (parentId: string) => void;
}) {
  const open = replyTo === entry.id;
  const nested = childrenOf(entries, entry.id);
  return (
    <article className={`al-office-msg${entry.authorRole === "instructor" ? " is-office" : ""}${open ? " is-open" : ""}`}>
      {canReply ? (
        <button
          type="button"
          className="al-office-msg-hit"
          aria-expanded={open}
          aria-label={`Reply to ${whoLabel(entry.authorName, entry.authorRole)}`}
          onClick={() => onOpen(entry.id)}
        >
          <MessageBody entry={entry} />
        </button>
      ) : (
        <div className="al-office-msg-hit">
          <MessageBody entry={entry} />
        </div>
      )}
      {open ? (
        <form
          className="al-office-pop"
          onSubmit={(event) => {
            event.preventDefault();
            onSend(entry.id);
          }}
        >
          <MessageField
            id={`office-pop-${entry.id}`}
            label="Reply to this message"
            value={popReply}
            onChange={onPopReply}
            placeholder="Write a reply"
            disabled={busy}
            textareaRef={popRef}
          />
          <button type="submit" disabled={busy || !messageReady(popReply)}>
            {busy ? "Sending…" : "Reply here"}
          </button>
        </form>
      ) : null}
      {nested.length > 0 ? (
        <div className="al-office-nest">
          {nested.map((child) => (
            <TimelineMessage
              key={child.id}
              entry={child}
              entries={entries}
              canReply={canReply}
              replyTo={replyTo}
              popReply={popReply}
              popRef={popRef}
              busy={busy}
              onOpen={onOpen}
              onPopReply={onPopReply}
              onSend={onSend}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}

function MessageBody({ entry }: { entry: OfficeEntry }) {
  return (
    <>
      <div className="al-office-item-top">
        <strong>{whoLabel(entry.authorName, entry.authorRole)}</strong>
        <span>{whenLabel(entry.at)}</span>
      </div>
      <MessageHtml body={entry.body} className="al-office-body" />
    </>
  );
}
